class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onFilterPillsMultiLabelClick = this.onFilterPillsMultiLabelClick.bind(this);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      this.onInputChange(event);
    });
    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    if (this.hasAttribute('data-filter-pills')) {
      this.addEventListener('click', this.onFilterPillsMultiLabelClick, true);
    }
  }

  /**
   * When a pill maps multiple facet values, clicking the label (not a checkbox)
   * should toggle every checkbox in that pill together.
   * @param {MouseEvent} event
   */
  onFilterPillsMultiLabelClick(event) {
    const multiHost = event.target.closest('[data-multi-filter-pill]');
    if (!multiHost || !this.contains(multiHost)) return;

    const inputs = [...multiHost.querySelectorAll('input[type="checkbox"][name]')].filter((i) => !i.disabled);
    if (inputs.length < 2) return;
    if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') return;

    const label = event.target.closest('label');
    if (!label || !multiHost.contains(label)) return;

    event.preventDefault();
    const nextChecked = !inputs.every((i) => i.checked);
    inputs.forEach((i) => {
      i.checked = nextChecked;
    });
    inputs[0].dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Escapes a string for safe use inside a CSS attribute selector.
   * @param {string} value
   * @returns {string}
   */
  static escapeForAttrSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  onInputChange(event) {
    this.debouncedOnSubmit(event);

    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;

    const multiHost = target.closest('[data-multi-filter-pill]');
    const grouped = multiHost
      ? [...multiHost.querySelectorAll('input[type="checkbox"][name]')].filter((i) => !i.disabled)
      : [];

    if (multiHost && grouped.length > 1) {
      grouped.forEach((el) => {
        el.checked = target.checked;
      });
    }

    const syncOne = (input) => {
      const name = input.name;
      const value = input.value;
      if (!name) return;
      const escapedName = FacetFiltersForm.escapeForAttrSelector(name);
      const escapedValue = FacetFiltersForm.escapeForAttrSelector(value);
      document
        .querySelectorAll(`input[type="checkbox"][name="${escapedName}"][value="${escapedValue}"]`)
        .forEach((el) => {
          if (el.disabled) return;
          el.checked = input.checked;
          el.removeAttribute('checked');
        });
    };

    if (multiHost && grouped.length > 1) {
      grouped.forEach(syncOne);
    } else {
      syncOne(target);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();


    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static async renderSectionFromFetch(url, event) {
    const response = await fetch(url);
    const responseText = await response.text();
    const html = responseText;
    FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    publish(PUB_SUB_EVENTS.facetsUpdate);

    FacetFiltersForm.scrollProductGridIntoView();
  }

  /**
   * Scrolls so the product grid sits just below the sticky header + facet toolbar.
   * Uses `[data-product-grid]` (the PLP list) and offsets by theme CSS variables and `#FacetFiltersTitle` height.
   */
  static scrollProductGridIntoView() {
    requestAnimationFrame(() => {
      const grid =
        document.querySelector('#ProductGridContainer [data-product-grid]') ||
        document.querySelector('[data-product-grid]');
      if (!grid) return;

      const rootStyles = getComputedStyle(document.documentElement);
      const parseCssLengthPx = (customPropertyName) => {
        const raw = rootStyles.getPropertyValue(customPropertyName).trim();
        const n = parseFloat(raw);
        return Number.isFinite(n) ? n : 0;
      };

      const stickyStackTop =
        parseCssLengthPx('--announcement-bar-height') + parseCssLengthPx('--header-height');
      const facetTitle = document.getElementById('FacetFiltersTitle');
      const facetToolbarHeight = facetTitle ? facetTitle.getBoundingClientRect().height : 0;
      const bufferPx = 10;
      const offsetAboveGrid = stickyStackTop + facetToolbarHeight + bufferPx;

      const gridTopDocument = grid.getBoundingClientRect().top + window.scrollY;
      const targetY = gridTopDocument - offsetAboveGrid;

      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: 'smooth',
      });
    });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    publish(PUB_SUB_EVENTS.facetsUpdate);

    FacetFiltersForm.scrollProductGridIntoView();
  }

  static renderProductGridContainer(result) {
    const gridContainer = document.getElementById('ProductGridContainer');
    const html = new DOMParser().parseFromString(result, 'text/html');

    gridContainer.querySelector('[data-product-grid]').innerHTML = html.querySelector('[data-product-grid]').innerHTML;
    gridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    const loadMore = html.querySelector('load-more');
    const gridLoadMore = gridContainer.querySelector('load-more')
    if (loadMore && gridLoadMore) {
      gridLoadMore.outerHTML = loadMore.outerHTML;
    }

    const facetRemoves = document.querySelectorAll('[data-facet-remove]');
    const htmlFacetRemove = html.querySelector('[data-facet-remove]');
    facetRemoves.forEach(facetRemove => {
      if (htmlFacetRemove) {
        facetRemove.innerHTML = htmlFacetRemove.innerHTML;
      }
    })


  }

  static renderProductCount(html) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const count = parsedHtml.querySelector('[data-product-count]')?.innerHTML || '';

    const containerDesktop = document.querySelector('[data-product-count]');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '.js-filter'
    );
    const facetsToRender = facetDetailsElementsFromFetch;

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.querySelector(`[data-input-id="${elementToRender.dataset.inputId}"]`);

      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      }

    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['[data-active-facets-desktop]'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  /**
   * Merges query strings from multiple facet forms without duplicating identical
   * key=value pairs. Several `<facet-filters-form>` instances often mirror the same
   * checked inputs (desktop / mobile / drawer); joining with `&` would repeat filters.
   * @param {...string} paramStrings Serialized `URLSearchParams` strings (may be empty).
   * @returns {string} Single query string safe for `history` and section fetch URLs.
   */
  static mergeSearchParamStrings(...paramStrings) {
    const valuesByKey = new Map();
    for (const str of paramStrings) {
      if (!str) continue;
      const sp = new URLSearchParams(str);
      for (const [key, value] of sp) {
        if (!valuesByKey.has(key)) valuesByKey.set(key, new Set());
        valuesByKey.get(key).add(value);
      }
    }
    const out = new URLSearchParams();
    for (const [key, valueSet] of valuesByKey) {
      for (const value of valueSet) {
        out.append(key, value);
      }
    }
    return out.toString();
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];

      sortFilterForms.forEach((form) => {
        forms.push(this.createSearchParams(form));
      });
      this.onSubmitForm(FacetFiltersForm.mergeSearchParamStrings(...forms), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);

class ToggleRadio extends HTMLElement {
  constructor() {
    super();
    this.radios = this.querySelectorAll('input[type="radio"]');

    this.radios.forEach(radio => {
      radio.addEventListener('change', this.handleChange.bind(this));
    });
  }

  toggleRadio(radio, mode = true) {
    let parent = radio.parentElement.querySelector('.svg-wrapper');
    let [add, remove] = mode ? ['fill', 'empty'] : ['empty', 'fill'];

    parent.classList.add(add);
    parent.classList.remove(remove);
  }

  handleChange(event) {
    this.radios.forEach(radio => {
      this.toggleRadio(radio, false);
    });
    this.toggleRadio(event.target);
  }

}

class GridToggleRadio extends ToggleRadio {
  constructor() {
    super();
  }

  get productGrid() {
    return document.querySelector('.product-grid-container');
  }

  toggleRadio(radio, mode = true) {
    super.toggleRadio(radio, mode);
    if (!mode) return;

    this.productGrid.dataset.layout = radio.value;
  }
}

customElements.define('grid-toggle-radio', GridToggleRadio);
